import { Search } from "lucide-react";
import { fmtAmt, fmtUsd, fmtPct, fmtPrice, ago, countdown, STATUS } from "../../lib/format";
import { Countdown } from "./Countdown";
import { useDealFilters, DEAL_FILTERS, DEAL_SORTS } from "../../hooks/useDealFilters";

const DealRow = ({ d, selected, onSelect }) => {
  const s = STATUS[d.status];
  return (
    <tr onClick={() => onSelect(d.id)} data-testid={`deal-card-${d.id}`}
      className={`row-hover border-b border-line/60 mono [&>td]:px-3 [&>td]:h-11 ${selected ? "bg-violet/10 shadow-[inset_2px_0_0_#C026F5]" : ""}`}>
      <td><div className="font-heading font-semibold text-sm text-white flex items-center gap-1.5">{d.token_symbol}<span className="tag border-violet/40 text-violet-300 h-4">/{d.stock}</span></div><div className="text-[10px] text-fog">{d.id}</div></td>
      <td><span className={`tag ${d.side === "sell" ? "border-neg/40 text-neg" : "border-pos/40 text-pos"}`}>{d.side === "sell" ? "ASK" : "BID"}</span></td>
      <td className="text-right text-white">{fmtAmt(d.token_amount)}<div className="text-[10px] text-fog">{d.slippage.pool_share_pct.toFixed(1)}% of pool</div></td>
      <td className="text-right text-white font-medium">{fmtPrice(d.price_usd)}</td>
      <td className="text-right text-fog">{fmtPrice(d.pool_price_usd)}</td>
      <td className={`text-right font-medium ${d.discount_pct > 0 ? "text-pos" : "text-neg"}`}>{d.discount_pct > 0 ? "−" : "+"}{Math.abs(d.discount_pct).toFixed(2)}%</td>
      <td className="text-right">{fmtUsd(d.notional_usd)}</td>
      <td className="text-right"><span className="text-violet-300 font-semibold" data-testid={`deal-saved-${d.id}`}>{d.slippage.saved_usd >= 0 ? "+" : ""}{fmtUsd(d.slippage.saved_usd)}</span><div className="text-[10px] text-fog">{fmtPct(d.slippage.impact_pct, 1)} impact avoided</div></td>
      <td><span className={`tag ${s.cls}`}>{s.label}</span></td>
      <td className="text-right text-fog">{ago(d.created_at)}</td>
      <td className="text-right text-fog">{d.status === "open" ? <Countdown seconds={d.seconds_to_expiry} /> : d.seconds_to_reclaim != null ? <span className="text-warn">reclaim {countdown(d.seconds_to_reclaim)}</span> : "—"}</td>
    </tr>
  );
};

export const DealBoard = ({ deals, isLoading, isError, onRetry, selectedId, onSelect, title = "Deal Board", emptyText }) => {
  const { rows, filter, setFilter, q, setQ, sort, setSort } = useDealFilters(deals);

  return (
    <section className="panel flex flex-col min-h-0 flex-1 max-lg:flex-none max-lg:h-[70vh]" data-testid="deal-board-container">
      <div className="panel-head max-lg:h-auto max-lg:flex-col max-lg:items-stretch max-lg:gap-2 max-lg:py-2">
        <div className="flex items-center gap-3">
          <h2 className="panel-title">{title}</h2>
          <span className="mono text-[11px] text-fog" data-testid="deal-board-count">{rows.length} listings</span>
        </div>
        <div className="flex items-center gap-2 max-lg:flex-wrap">
          <div className="flex bg-ink rounded border border-line2 p-0.5">
            {DEAL_FILTERS.map(([id, label]) => (
              <button key={id} onClick={() => setFilter(id)} data-testid={`deal-filter-${id}`}
                className={`h-6 px-2 rounded-sm text-[11px] font-medium transition-colors ${filter === id ? "bg-violet text-white" : "text-fog hover:text-white"}`}>{label}</button>
            ))}
          </div>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-2 text-fog" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Token / stock / ID" className="field h-7 pl-7 w-36 text-xs" data-testid="deal-board-search-input" />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="field h-7 w-auto text-xs pr-6" data-testid="deal-board-sort-select">
            {DEAL_SORTS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="overflow-auto flex-1">
        <table className="w-full min-w-[880px] text-xs">
          <thead className="sticky top-0 bg-s1 z-10">
            <tr className="eyebrow text-left [&>th]:px-3 [&>th]:h-8 [&>th]:font-normal border-b border-line">
              <th>Token · pair</th><th>Side</th><th className="text-right">Size</th><th className="text-right">Offered · USD/M</th><th className="text-right">Pool · USD/M</th>
              <th className="text-right">Δ pool</th><th className="text-right">Notional</th><th className="text-right">Impact saved</th><th>Status</th><th className="text-right">Posted</th><th className="text-right">Expires</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={11} className="p-8 text-center text-fog">Loading listings…</td></tr>}
            {isError && !isLoading && (
              <tr><td colSpan={11} className="p-10 text-center" data-testid="deal-board-error">
                <div className="text-neg font-medium">Couldn't load listings.</div>
                <div className="text-slate-400 text-xs mt-1">The backend didn't respond. Check your connection and try again.</div>
                {onRetry && <button onClick={onRetry} className="btn-ghost h-8 px-3 text-xs mt-3" data-testid="deal-board-retry-btn">Retry</button>}
              </td></tr>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <tr><td colSpan={11} className="p-10 text-center text-slate-400" data-testid="deal-board-empty">{emptyText || "No open listings match. Post one from the Escrow Ticket on the right, or clear the filter."}</td></tr>
            )}
            {!isError && rows.map((d) => <DealRow key={d.id} d={d} selected={d.id === selectedId} onSelect={onSelect} />)}
          </tbody>
        </table>
      </div>
    </section>
  );
};
